import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  AppBar,
  Toolbar,
  Grid,
  FormControl,
  Tooltip,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  Alert,
  Snackbar,
  styled,
  DialogTitle,
  DialogActions,
  DialogContent,
  Table,
  TableHead,
  TableBody,
  Chip,
  TableCell,
  TableContainer,
  TableRow,
  ButtonGroup,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate, useParams } from "react-router-dom";
import jsPDF from "jspdf";
import { TokenOutlined } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import base64 from "base64-js";

const AdmitCard = () => {
  const [isFeeCheckLoading, setIsFeeCheckLoading] = useState(false);
const [dueDialogOpen, setDueDialogOpen] = useState(false);
const [dueData, setDueData] = useState(null);
  const [canWrite, setCanWrite] = useState(false);
  const [canPrint, setCanPrint] = useState(false);
  const { encodedFormId } = useParams();
  const navigate = useNavigate();
  const [authorization, setAuthorization] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [examTypes, setExamTypes] = useState([]);
  const [subExams, setSubExams] = useState([]);
  const [selectedExamType, setSelectedExamType] = useState("");
  const [selectedSubExam, setSelectedSubExam] = useState("");
  const [classNames, setClassNames] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState("");
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState("");
  const [selectedSession, setSelectedSession] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [students, setStudents] = useState([]);
  const [isExamModeIndependent, setIsExamModeIndependent] = useState(0);
  const [isSubExamDisabled, setIsSubExamDisabled] = useState(false);
  const [openIndependentDialog, setOpenIndependentDialog] = useState(false);

  const MONTH_NAMES = ["", "January","February","March","April","May","June","July","August","September","October","November","December"];
const formatDueDate = (val) => {
  if (!val) return "--";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "--" : d.toLocaleDateString();
};

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      // Check if the string is a valid base64 string
      if (!/^[A-Za-z0-9+/=]+$/.test(encodedFormId)) {
        console.error("Invalid base64 string:", encodedFormId);
        return null;
      }
      const paddedEncodedFormId = encodedFormId.padEnd(
        Math.ceil(encodedFormId.length / 4) * 4,
        "="
      );
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
  if (!formId) {
    console.error("formId is null, cannot call Authorizer");
    setAuthorization({ isPermission: 0 });
    showSnackbar("Invalid form ID. Please try again.", "error");
    return;
  }
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({
        formId,
      }),
    });
    if (response.ok) {
      const responseData = await response.json();
      if (responseData && responseData.length > 0) {
        const authorizationData = responseData[0];
        setAuthorization(authorizationData);
        setCanPrint(authorizationData.uPrint === 1);
        setCanWrite(authorizationData.uWrite === 1); // Set write permission
      } else {
        console.error("No authorization data returned");
        setAuthorization({ isPermission: 0 });
        showSnackbar("No authorization data found.", "error");
      }
    } else {
      console.error("Authorization failed:", response.status);
      showSnackbar("Invalid authorization response.", "error");
      setAuthorization({ isPermission: 0 });
    }
  } catch (error) {
    console.error("API request error:", error);
    showSnackbar("An error occurred during authorization.", "error");
    setAuthorization({ isPermission: 0 });
  } finally {
    setIsLoading(false);
  }
};

    // Add this useEffect to clear students when any filter changes
  useEffect(() => {
    setStudents([]);
  }, [selectedExamType, selectedSubExam, selectedClass, selectedSection, session]);

  // Update the class change handler to also clear section
  const handleClassChange = (e) => {
    setSelectedClass(e.target.value);
    setSelectedSection(""); // Clear section when class changes
  };


  useEffect(() => {
    if (!encodedFormId) {
      console.error("encodedFormId is missing");
      setAuthorization({ isPermission: 0 });
      showSnackbar("Invalid form ID in URL.", "error");
      setIsLoading(false);
      return;
    }
    if (formId) {
      Authorizer();
    } else {
      setAuthorization({ isPermission: 0 });
      showSnackbar("Failed to decode form ID.", "error");
      setIsLoading(false);
    }
  }, [formId, encodedFormId]);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

 const handlePrintClick = async (studentId) => {
  if (!session) return;
  setIsFeeCheckLoading(true);
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${apiUrl}/Result/StudentResultWithFeeCheck`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify({
        studentId,
        sessionId: session,
        examTypeId: selectedExamType,
        subExamId: isSubExamDisabled ? 0 : selectedSubExam,
        subjectId: 0,
      }),
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    if (data.canViewResult) {
      handlePrint(studentId);
    } else {
      setDueData({ ...data, studentId }); // keep studentId for the bypass button
      setDueDialogOpen(true);
    }
  } catch (error) {
    console.error("Error checking fee status:", error);
    setSnackbarMessage("Error checking fee status. Please try again.");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  } finally {
    setIsFeeCheckLoading(false);
  }
}; 

const handleCloseDueDialog = () => {
  setDueDialogOpen(false);
  setDueData(null);
};

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Navigate to "Create New Admit"
  const handleNavigateToCreateNewAdmit = () => {
    navigate(`/admitpage/${encodedFormId}`); // Use the original encodedFormId
  };

  const handleNavigateToAdmitCard = () => {
    navigate(`/admitcard/${encodedFormId}`); // Use the original encodedFormId
  };


  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 20,
    padding: "8px 24px",
    textTransform: "none",
    fontWeight: 600,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    },
  }));

  useEffect(() => {
    const fetchExamTypes = async () => {
      try {
        const Url = process.env.REACT_APP_BASE_URL;
        const apiUrl = `${Url}/ExaminationCriteria/GetExaminationCriteria`;
        const token = sessionStorage.getItem("token");
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ examCategoryId: 0 }),
        });
        const data = await response.json();
        if (data && Array.isArray(data)) {
          setExamTypes(data);
        } else {
          setSnackbarMessage("No exam types found.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Error fetching exam types:", error);
        setSnackbarMessage("Failed to fetch exam types. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };
    fetchExamTypes();
  }, []);

  useEffect(() => {
    if (!selectedExamType) return;
    const selectedExam = examTypes.find(
      (exam) => exam.examCriteriaId === selectedExamType
    );
    if (selectedExam) {
      setIsExamModeIndependent(selectedExam.isExamModeIndependent);
      if (selectedExam.isExamModeIndependent === 1) {
        setIsSubExamDisabled(true);
        setSelectedSubExam("");
        setSubExams([]);
        setSnackbarMessage(
          "Sub-exam selection is disabled as the exam mode is independent."
        );
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
      } else {
        setIsSubExamDisabled(false);
      }
    }
  }, [selectedExamType, examTypes]);

  useEffect(() => {
    if (!selectedExamType || isExamModeIndependent === 1) return;
    const fetchSubExams = async () => {
      try {
        const Url = process.env.REACT_APP_BASE_URL;
        const apiUrl = `${Url}/Teacher/ddlExamSubCategory_Examination`;
        const token = sessionStorage.getItem("token");
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ examCategoryId: selectedExamType }),
        });
        const data = await response.json();
        if (data && Array.isArray(data)) {
          const mappedSubExams = data.map((subExam) => ({
            id: subExam.examSubCategoryId,
            name: subExam.examSubCategory,
          }));
          setSubExams(mappedSubExams);
        }
      } catch (error) {
        console.error("Error fetching sub exams:", error);
        setSnackbarMessage("Failed to fetch sub-exams. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };
    fetchSubExams();
  }, [selectedExamType, isExamModeIndependent]);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const apiUrl = `${process.env.REACT_APP_BASE_URL}/ClassPromotion/GetFinancialYear`;
        const token = sessionStorage.getItem("token");
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({}),
        });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          const mappedSessions = data.map((session) => ({
            id: session.financialYearID,
            name: session.finanacialYear,
          }));
          setSessions(mappedSessions);
          const today = new Date();
          const currentYear = today.getFullYear();
          const nextYear = currentYear + 1;
          const financialYearStart = new Date(currentYear, 3, 1);
          const financialYearEnd = new Date(nextYear, 2, 31);
          let currentSessionString =
            today >= financialYearStart && today <= financialYearEnd
              ? `${currentYear}-${nextYear}`
              : `${currentYear - 1}-${currentYear}`;
          const currentSession = mappedSessions.find(
            (session) => session.name === currentSessionString
          );
          if (currentSession) {
            setSession(currentSession.id);
          }
        } else {
          setSnackbarMessage("No sessions found.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Error fetching financial years:", error);
        setSnackbarMessage("Failed to fetch sessions. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };
    fetchSessions();
  }, []);

  useEffect(() => {
    const fetchClassNames = async () => {
      try {
        const Url = process.env.REACT_APP_BASE_URL;
        const apiUrl = `${Url}/CreateClass/GetClassName`;
        const token = sessionStorage.getItem("token");
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({}),
        });
        const data = await response.json();
        if (data && Array.isArray(data)) {
          const mappedClassNames = data.map((classData) => ({
            id: classData.classId,
            name: classData.className,
          }));
          setClassNames(mappedClassNames);
        } else {
          setSnackbarMessage("No classes found.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Error fetching class names:", error);
        setSnackbarMessage("Failed to fetch classes. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };
    fetchClassNames();
  }, []);

  useEffect(() => {
    const fetchSections = async () => {
      if (!selectedClass) return;
      try {
        const Url = process.env.REACT_APP_BASE_URL;
        const apiUrl = `${Url}/Exam/ddlSection_clsId`;
        const token = sessionStorage.getItem("token");
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ classId: selectedClass }),
        });
        const data = await response.json();
        if (data && Array.isArray(data)) {
          const mappedSections = data.map((section) => ({
            id: section.sectionId,
            name: section.sectionName,
          }));
          setSections(mappedSections);
        } else {
          setSnackbarMessage("No sections found for the selected class.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Error fetching sections:", error);
        setSnackbarMessage("Failed to fetch sections. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };
    fetchSections();
  }, [selectedClass]);

  const handleGetDetails = async () => {
    if (
      !selectedExamType ||
      (!isSubExamDisabled && !selectedSubExam) ||
      !selectedClass ||
      !selectedSection ||
      !session
    ) {
      setOpenDialog(true);
      return;
    }
    setIsLoading(true);
    const payload = {
      examCategoryId: selectedExamType,
      subExamId: isSubExamDisabled ? 0 : selectedSubExam,
      classId: selectedClass,
      sectionId: selectedSection,
      sessionId: session,
    };
    try {
      const Url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${Url}/AdmitCard/GetGeneratedAdmitcarAll`;
      const token = sessionStorage.getItem("token");
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.data === null && data.msg === "Record Not Found") {
        setStudents([]);
        setSnackbarMessage(
          "No admit card details found for the selected criteria."
        );
        setSnackbarSeverity("warning");
      } else if (data && Array.isArray(data.data) && data.data.length > 0) {
        setStudents(data.data);
        setSnackbarMessage("Admit card data retrieved successfully!");
        setSnackbarSeverity("success");
      } else {
        setStudents([]);
        setSnackbarMessage("No data found.");
        setSnackbarSeverity("warning");
      }
    } catch (error) {
      console.error("Error fetching admit card details:", error);
      setSnackbarMessage("Failed to fetch admit card details. Please try again.");
      setSnackbarSeverity("error");
    } finally {
      setIsLoading(false);
      setSnackbarOpen(true);
    }
  };

  const handlePrint = async (studentId) => {
    try {
      const schoolName =
        sessionStorage.getItem("organizationName")?.replace(/['"]+/g, "") ||
        "IDEAL PUBLIC SCHOOL";
      const logo = sessionStorage.getItem("clientLogo");
      const logoUrl = `https://wsms.aidigitalsec.com${logo?.replace("~", "") || ""
        }`;
      const schoolAddress =
        sessionStorage.getItem("address")?.replace(/['"]+/g, "") ||
        "STREET NO 1 2 B- BLOCK SARUP VIHAR";
      const contactInfo =
        sessionStorage.getItem("phoneNo")?.replace(/['"]+/g, "") ||
        "1234567890";

      const Url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${Url}/AdmitCard/PrintAdmitCard`;
      const token = sessionStorage.getItem("token");
      const payload = {
        examTypeId: selectedExamType,
        subExamId: isSubExamDisabled ? 0 : selectedSubExam,
        classId: selectedClass,
        sectionId: selectedSection,
        sessionId: session,
        studentId: studentId,
      };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.statusText}`);
      }

      const apiData = await response.json();
      if (!apiData || !apiData.data || !apiData.data.length) {
        throw new Error("No admit card data found.");
      }

      const admitCardData = apiData.data[0];
      const doc = new jsPDF();

      doc.setDrawColor("#000");
      doc.setLineWidth(0.5);
      doc.rect(10, 10, 192, 277);

      const headerColor = "#2E7D32";
      const textColor = "#000";

      doc.setFont("Helvetica", "bold");
      doc.setFontSize(14);
      doc.setTextColor(headerColor);
      doc.text(schoolName, 105, 20, { align: "center" });
      doc.setFontSize(10);
      doc.setTextColor(textColor);
      doc.text(schoolAddress, 105, 28, { align: "center" });
      doc.text(contactInfo, 105, 34, { align: "center" });

      doc.setDrawColor("#000");
      doc.setLineWidth(0.2);
      doc.line(20, 38, 190, 38);

      doc.setFontSize(13);
      doc.setFont("Helvetica", "bold");
      doc.setTextColor("#333");
      doc.text(
        `${admitCardData.examType || "N/A"}${typeof admitCardData.subExam === "string" ? " - " + admitCardData.subExam : ""}`,
        105,
        50,
        { align: "center" }
      );

      doc.setDrawColor("#2E7D32");
      doc.setLineWidth(0.8);
      doc.line(55, 53, 155, 53);

      doc.setLineWidth(0.2);
      doc.setDrawColor("#000");

      doc.setFontSize(10);
      doc.setFont("Helvetica", "normal");

      const detailsTopMargin = 65;
      const studentDetails = [
        `NAME: ${admitCardData.studentName || "N/A"}`,
        `FATHER'S NAME: ${admitCardData.fathersName || "N/A"}`,
        `CLASS/GRADE: ${admitCardData.className || "N/A"}`,
        `SECTION: ${admitCardData.sectionName || "N/A"}`,
        `ROLL NUMBER: ${admitCardData.rollNo || "N/A"}`,
        `ADMISSION NO: ${admitCardData.admissionNo || "N/A"}`,
      ];

      const detailPositions = [
        { x: 20, y: detailsTopMargin },
        { x: 20, y: detailsTopMargin + 6 },
        { x: 102, y: detailsTopMargin },
        { x: 105, y: detailsTopMargin + 6 },
        { x: 193, y: detailsTopMargin },
        { x: 193, y: detailsTopMargin + 6 },
      ];

      studentDetails.forEach((detail, index) => {
        const position = detailPositions[index];
        doc.text(detail, position.x, position.y, {
          align: index >= 4 ? "right" : "left",
        });
      });

      const headers = [
        "SUBJECT",
        "DATE",
        "START TIME",
        "END TIME",
        "INVIGILATOR'S SIGN",
      ];

      const convertToDateFormat = (date) => {
        if (!date) return "N/A";
        const [year, month, day] = date.split("-").map(Number);
        return `${day.toString().padStart(2, "0")}/${month
          .toString()
          .padStart(2, "0")}/${year}`;
      };

      const convertTo12HourFormat = (time) => {
        if (!time) return "N/A";
        const [hours, minutes] = time.split(":").map(Number);
        const period = hours >= 12 ? "PM" : "AM";
        const adjustedHours = hours % 12 || 12;
        return `${adjustedHours}:${minutes
          .toString()
          .padStart(2, "0")} ${period}`;
      };

      const tableData = apiData.data.map((entry) => [
        entry.subjectName || "N/A",
        convertToDateFormat(entry.firstExamDate),
        convertTo12HourFormat(entry.examStartTime),
        convertTo12HourFormat(entry.examEndTime),
        entry.remarks || "",
      ]);

      let tableTopMargin = 5;
      let startY = 74 + tableTopMargin;
      doc.setFontSize(10);
      doc.setTextColor(textColor);

      doc.setFillColor(headerColor);
      doc.setTextColor("#FFF");
      doc.rect(19.5, startY, 176, 10, "F");

      const columnWidths = [50, 30, 30, 30, 35];
      const tableStartX = 20;
      const tableHeaderHeight = 10;

      headers.forEach((header, index) => {
        const xPosition =
          tableStartX + columnWidths.slice(0, index).reduce((a, b) => a + b, 0);
        doc.text(header, xPosition + columnWidths[index] / 2, startY + 7, {
          align: "center",
        });
      });

      tableData.forEach((row, rowIndex) => {
        let currentX = tableStartX;
        const rowY = startY + (rowIndex + 1) * tableHeaderHeight;
        row.forEach((cell, cellIndex) => {
          const columnWidth = columnWidths[cellIndex];
          doc.rect(currentX, rowY, columnWidth, tableHeaderHeight);
          doc.setTextColor("#000");
          doc.text(cell, currentX + columnWidth / 2, rowY + 7, {
            align: "center",
          });
          currentX += columnWidth;
        });
      });

      const lineY = startY + 100;
      doc.setDrawColor("#000");
      doc.line(20, lineY - 5, 70, lineY - 5);
      doc.line(80, lineY - 5, 130, lineY - 5);
      doc.line(140, lineY - 5, 190, lineY - 5);

      doc.setFont("Helvetica", "normal");
      doc.text("CLASS TEACHER", 30, lineY);
      doc.text("CLERK", 100, lineY);
      doc.text("PRINCIPAL/DEAN", 150, lineY);

      const wrapText = (doc, text, x, y, maxWidth) => {
        const lines = doc.splitTextToSize(text, maxWidth);
        let currentY = y;
        lines.forEach((line) => {
          doc.text(line, x, currentY);
          currentY += 6;
        });
        return currentY;
      };

      const boxTopMargin = 40;
      const dividerY = lineY + boxTopMargin - 25;
      doc.setDrawColor("#000");
      doc.setLineWidth(0.5);
      doc.line(10, dividerY, 202, dividerY);

      doc.setLineWidth(0.2);

      const headingY = lineY + boxTopMargin - 6;
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor("#333");
      doc.text("Important Instructions:", 25, headingY);

      const boxTopY = lineY + boxTopMargin;
      const boxHeight = 60;
      doc.setDrawColor("#000");
      doc.rect(20, boxTopY, 170, boxHeight);

      const instructions = [
        "1. Students must carry this admit card to the examination hall.",
        "2. Ensure all details on this admit card are correct.",
        "3. Report to the examination hall 30 minutes before the start time.",
        "4. Follow all rules and instructions during the examination.",
        "5. Personal belongings such as bags, mobile phones, and electronic devices are not allowed in the examination hall.",
        "6. Students must sit in the assigned seat as per the roll number.",
        "7. Maintain silence during the exam and raise your hand if you need assistance from the invigilator.",
      ];

      doc.setFontSize(10);
      doc.setFont("Helvetica", "normal");
      doc.setTextColor("#000");

      let instructionY = boxTopY + 8;
      const maxWidth = 150;

      instructions.forEach((instruction) => {
        instructionY = wrapText(doc, instruction, 25, instructionY, maxWidth);
        if (instructionY > boxTopY + boxHeight - 8) {
          console.warn("Text is overflowing, consider increasing box height.");
          return;
        }
      });

      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 5000);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handleSessionChange = (event) => setSession(event.target.value);

  if (!authorization) {
    return <div>Loading...</div>;
  }

  if (authorization.isPermission !== 1 || !formId) {
    return <div>You are not authorized to view this page</div>;
  }

  return (
    <Box p={3}>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Admit Card <InfoIcon onClick={() => setShowUserGuide(true)} style={{ opacity: "0.6" }} />
          </Typography>
          <Dialog
            open={showUserGuide}
            onClose={() => setShowUserGuide(false)}
            maxWidth="md"
            fullWidth
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: "12px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            <DialogTitle
              sx={{
                background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)",
                color: "white",
                fontWeight: "bold",
                textAlign: "center",
                borderRadius: "12px 12px 0 0",
                py: 2,
              }}
            >
              Admit Card User Guide
            </DialogTitle>
            <DialogContent sx={{ padding: "24px" }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#1898c2",
                  fontWeight: "600",
                  mb: 2,
                  mt: 1,
                  textAlign: "center",
                }}
              >
                Quick Steps to Get Admit Cards
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyleType: "none",
                  padding: 0,
                  "& li": {
                    display: "flex",
                    alignItems: "flex-start",
                    mb: 1.5,
                    fontSize: "1.0rem",
                    color: "#333",
                  },
                  "& li::before": {
                    content: '"\\2022"',
                    color: "#1898c2",
                    fontWeight: "bold",
                    display: "inline-block",
                    width: "1.5em",
                    mr: 1,
                    fontSize: "1.2rem",
                  },
                  "& strong": {
                    margin: "0 0.2em",
                  },
                  "& em": {
                    margin: "0 0.2em",
                  },
                }}
              >
                <li>Select an <strong>Exam Type</strong> from the dropdown menu.</li>
                <li>If applicable, select a <strong>Sub Exam</strong> from the dropdown menu.</li>
                <li>Choose a <strong>Session</strong> from the dropdown menu.</li>
                <li>Select a <strong>Class</strong> from the dropdown menu.</li>
                <li>Select a <strong>Section</strong> from the dropdown menu.</li>
                <li>Click the <strong>GET ADMIT DETAILS</strong> button to retrieve the list of students based on the selected criteria.</li>
                <li>View the list of students and their details in the table.</li>
                <li>To print an admit card for a student, click the <strong>Print</strong> button next to the student's details.</li>
                <li>Follow the instructions on the printed admit card for examination guidelines.</li>
                <li>To create a new admit card, click <strong>CREATE NEW ADMIT</strong> button and follow the steps to generate a new admit card.</li>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
              <StyledButton
                onClick={() => setShowUserGuide(false)}
                color="primary"
                variant="contained"
                sx={{
                  backgroundColor: "#1898c2",
                  "&:hover": { backgroundColor: "#156f9a" },
                  borderRadius: "8px",
                  px: 4,
                }}
              >
                Close
              </StyledButton>
            </DialogActions>
          </Dialog>
        </Toolbar>
      </AppBar>
      <div
        style={{
          display: "flex",
          marginBottom: "5px",
          marginTop: 20,
          marginLeft: 80,
        }}
      >
        <ButtonGroup>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNavigateToAdmitCard}
          >
            Admit Card
          </Button>
          <Tooltip title={canWrite ? "Create New Admit Card" : "Not authorized to Create"} arrow>
         <span><Button
  variant="outlined"
  color="primary"
  onClick={handleNavigateToCreateNewAdmit}
  disabled={!canWrite}
>
  Create New Admit
</Button></span></Tooltip>

        </ButtonGroup>
      </div>
      <Paper
        elevation={3}
        sx={{ padding: 4, margin: "auto", maxWidth: 1000, marginTop: 3 }}
      >
        <Grid
          container
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="exam-type-label">Exam Type</InputLabel>
              <Select
                labelId="exam-type-label"
                id="exam-type"
                value={selectedExamType}
                onChange={(e) => setSelectedExamType(e.target.value)}
                label="Exam Type"
              >
                {examTypes.map((type) => (
                  <MenuItem
                    key={type.examCriteriaId}
                    value={type.examCriteriaId}
                  >
                    {type.examType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth disabled={isSubExamDisabled}>
              <InputLabel id="sub-exam-label">Sub Exam</InputLabel>
              <Select
                labelId="sub-exam-label"
                id="sub-exam"
                value={selectedSubExam}
                onChange={(e) => setSelectedSubExam(e.target.value)}
                label="Sub Exam"
              >
                {subExams.map((subExam) => (
                  <MenuItem key={subExam.id} value={subExam.id}>
                    {subExam.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="session-label">Session</InputLabel>
              <Select
                labelId="session-label"
                id="session"
                value={session}
                onChange={handleSessionChange}
                label="Session"
              >
                {sessions.map((session) => (
                  <MenuItem key={session.id} value={session.id}>
                    {session.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box mt={4} display="flex" justifyContent="center" gap={2}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="class-label">Class</InputLabel>
            <Select
              labelId="class-label"
              id="class"
              value={selectedClass}
              onChange={handleClassChange}
              label="Class"
            >
              {classNames.map((className) => (
                <MenuItem key={className.id} value={className.id}>
                  {className.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="section-label">Section</InputLabel>
            <Select
              labelId="section-label"
              id="section"
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              label="Section"
            >
              {sections.map((section) => (
                <MenuItem key={section.id} value={section.id}>
                  {section.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box mt={4} display="flex" justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleGetDetails}
          >
            GET ADMIT DETAILS
          </Button>
        </Box>
        <Box mt={4}>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Student Name
                  </TableCell>
                  <TableCell
                    style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Roll No
                  </TableCell>
                  <TableCell
                    style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Admission No
                  </TableCell>
                  <TableCell
                    style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Father's Name
                  </TableCell>
                  <TableCell
                    style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Mobile No
                  </TableCell>
                  <TableCell
                    style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No admit details found.
                    </TableCell>
                  </TableRow>
                ) : (
                  students.map((student, index) => (
                    <TableRow key={index}>
                      <TableCell>{student.studentName || "N/A"}</TableCell>
                      <TableCell>{student.rollNo || "N/A"}</TableCell>
                      <TableCell>{student.admissionNo || "N/A"}</TableCell>
                      <TableCell>
                        {student.fathersName &&
                          typeof student.fathersName === "string"
                          ? student.fathersName
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        {student.mobileNo &&
                          typeof student.mobileNo === "string"
                          ? student.mobileNo
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                         <Tooltip title={canPrint ? "Print Admit Card" : "Not authorized to Print"} arrow>
                        <span><Button
  variant="contained"
  color="primary"
  onClick={() => handlePrintClick(student.studentId)}
  disabled={!canPrint || isFeeCheckLoading}
>
  {isFeeCheckLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Print"}
</Button></span></Tooltip>
                      </TableCell>

                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <Dialog open={openDialog} onClose={handleDialogClose}>
          <DialogTitle>{"Missing Information"}</DialogTitle>
          <DialogContent>
            Please select all required details before proceeding.
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
      <Dialog open={dueDialogOpen} onClose={handleCloseDueDialog} maxWidth="sm" fullWidth>
  <DialogTitle sx={{ backgroundColor: "#b22222", color: "#fff", fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}>
    <WarningAmberIcon /> Admit Card Unavailable
  </DialogTitle>
  <DialogContent sx={{ padding: "24px" }}>
    <Typography sx={{ color: "red", fontWeight: "bold", mb: 1, mt: 1}}>
      {dueData?.message || "The admit card cannot be generated because there are unpaid fee dues."}
    </Typography>
    <Typography sx={{ fontWeight: 700, mb: 2 }}>
      Total Due Amount: ₹{dueData?.totalDueAmount ?? 0}
    </Typography>
    {dueData?.dueMonths?.length > 0 && (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Month</TableCell><TableCell align="right">Total Fee</TableCell>
            <TableCell align="right">Paid</TableCell><TableCell align="right">Balance</TableCell>
            <TableCell align="center">Status</TableCell><TableCell align="center">Payment Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {dueData.dueMonths.map((m, idx) => (
            <TableRow key={`${m.monthId}-${idx}`}>
              <TableCell>{MONTH_NAMES[m.monthId] ?? m.monthId}</TableCell>
              <TableCell align="right">{m.totalFee}</TableCell>
              <TableCell align="right">{m.paidAmount}</TableCell>
              <TableCell align="right">{m.balance}</TableCell>
              <TableCell align="center">
                <Chip label={m.paidStatus === 1 ? "Paid" : "Unpaid"} size="small" color={m.paidStatus === 1 ? "success" : "error"} />
              </TableCell>
              <TableCell align="center">{formatDueDate(m.feePaymentDate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
  </DialogContent>
 <DialogActions sx={{ padding: "16px 24px" }}>
  <Button onClick={handleCloseDueDialog} variant="outlined" color="inherit">
    Close
  </Button>
  <Button
    onClick={() => {
      setDueDialogOpen(false);
      handlePrint(dueData?.studentId);
    }}
    variant="contained"
    color="error"
  >
    Print Anyway
  </Button>
</DialogActions>
</Dialog>
    </Box>
  );
};

export default AdmitCard;
